import { useCallback } from 'react';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { bbkApiSlice } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { useCreatePollingRequest } from '@bbkAdminRedux/rtkq/polling.slice';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';

/** Platform-level locale exposed by `GET /api/v2/languages/available`. */
export type AvailableLocale = {
  locale_code: string;
  display_name: string;
  rtl: boolean;
  enabled: boolean;
  sort_order: number;
};

/** Exportable resources (`BRAND_LABELS` not wired yet). */
export type TranslatableResource =
  | 'OFFER'
  | 'LOSS_AVERSION_CARD'
  | 'CONFIRMATION_PAGE'
  | 'CANCEL_PAGE'
  | 'VANITY_REASON';

export type TranslationExportSelection = {
  resource: TranslatableResource;
  resource_id: string;
};

export type TranslationExportOptions = {
  content_scope: 'ALL' | 'SELECTED';
  csv_format?: 'PLAIN' | 'BOM';
  /** Omit for all enabled non-base locales. */
  locale?: string | null;
  selections?: TranslationExportSelection[];
};

export type ExportTranslationsArgs = {
  appKey: string;
  translation_export: TranslationExportOptions;
};

/**
 * Locale enabled on an application, returned by `GET /api/v2/apps/{appKey}/languages`.
 *
 * `rule_id` is the hashids-encoded id of the attached `LOCALE_MATCHING` rule (matching the
 * encoding used by other API IDs e.g. `Rule.application_id`). `null` / `undefined` mean the
 * locale currently has no matching rule attached.
 *
 * `client_formatted_rule` is the Brightback expression for the attached rule, included by the
 * languages list endpoint so the matching-rule cell can render on page load without a follow-up
 * fetch. Null / undefined when the locale has no rule (or its rule was deleted / inactivated).
 */
export type ApplicationLocale = {
  locale_code: string;
  base: boolean;
  enabled: boolean;
  rule_id?: string | null;
  client_formatted_rule?: string | null;
};

export type LanguageSourceType = 'API_JS' | 'BILLING';

export type LanguageSourceConfig = {
  source_type: LanguageSourceType;
  field_key: string;
  enabled: boolean;
};

type AppKeyArg = { appKey: string };
type LocaleCodeArg = AppKeyArg & { localeCode: string };

/**
 * Args for the consolidated `PUT /api/v2/apps/{app}/languages/{localeCode}` endpoint.
 * Every field is optional; at least one must be present.
 *
 * - `enabled: false` deactivates the locale.
 * - `enabled: true` (or omitted alongside other fields) ensures the locale row exists & is enabled.
 * - `base: true` promotes the locale to the base / fallback locale.
 * - `clientFormattedRule` (non-empty, only honored when `enabled !== false`) creates or updates
 *   the `LOCALE_MATCHING` rule attached to the locale.
 */
export type UpsertApplicationLocaleArgs = LocaleCodeArg & {
  base?: boolean;
  enabled?: boolean;
  clientFormattedRule?: string;
};

export const languagesSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getAvailableLocales: build.query<AvailableLocale[], void>({
      query: () => `/api/v2/languages/available`,
    }),
    getApplicationLocales: build.query<ApplicationLocale[], AppKeyArg>({
      query: ({ appKey }) => `/api/v2/apps/${appKey}/languages`,
      providesTags: ['ApplicationLocales'],
    }),
    upsertApplicationLocale: build.mutation<
      ApplicationLocale,
      UpsertApplicationLocaleArgs
    >({
      query: ({ appKey, localeCode, base, enabled, clientFormattedRule }) => ({
        url: `/api/v2/apps/${appKey}/languages/${localeCode}`,
        method: 'PUT',
        body: {
          base,
          enabled,
          client_formatted_rule: clientFormattedRule,
        },
      }),
      invalidatesTags: ['ApplicationLocales'],
    }),
    getLanguageSourceConfig: build.query<
      LanguageSourceConfig | null,
      AppKeyArg
    >({
      query: ({ appKey }) => `/api/v2/apps/${appKey}/language-source`,
      providesTags: ['LanguageSourceConfig'],
    }),
    configureLanguageSource: build.mutation<
      LanguageSourceConfig,
      AppKeyArg & {
        sourceType: LanguageSourceType;
        fieldKey: string;
        enabled: boolean;
      }
    >({
      query: ({ appKey, sourceType, fieldKey, enabled }) => ({
        url: `/api/v2/apps/${appKey}/language-source`,
        method: 'PUT',
        body: {
          source_type: sourceType,
          field_key: fieldKey,
          enabled,
        },
      }),
      invalidatesTags: ['LanguageSourceConfig'],
    }),
    /** Starts a translation CSV export; response includes poll `statusURL`. */
    exportTranslations: build.query<
      { statusURL: string },
      ExportTranslationsArgs
    >({
      query: ({ appKey, translation_export }) => ({
        url: `/api/v1/apps/${appKey}/translations/export`,
        method: 'POST',
        body: { translation_export },
      }),
    }),
  }),
});

export const {
  useGetAvailableLocalesQuery,
  useGetApplicationLocalesQuery,
  useUpsertApplicationLocaleMutation,
  useGetLanguageSourceConfigQuery,
  useConfigureLanguageSourceMutation,
} = languagesSlice;

/** POST export → poll → download CSV. */
export const useExportTranslations = () => {
  const [exportTranslations$] =
    languagesSlice.endpoints.exportTranslations.useLazyQuery();
  const pollExport = useCreatePollingRequest(exportTranslations$);

  return useCallback(
    async (args: ExportTranslationsArgs) => {
      BlackToastTrigger({
        content:
          'Export being generated. Please wait. A copy will also be emailed when ready.',
        options: { autoClose: 8000 },
      });

      try {
        const downloadUrl = await pollExport(args);
        BlackToastTrigger({ content: 'Export successfully generated.' });
        window.location.href = downloadUrl;
      } catch (err: unknown) {
        let content = 'An error occurred during export, please try again.';
        if (err instanceof Error) {
          if (err.message === 'failed') {
            content = 'Export failed, please try again.';
          } else if (err.message === 'expired') {
            content = 'Export link expired, please try again.';
          } else if (
            err.message === 'timeout' ||
            err.message === 'unknown status'
          ) {
            content = 'It took too long to generate file, please try again.';
          }
        }
        GlobalLogger.error(err);
        BlackToastTrigger({
          content,
          options: { autoClose: 6000 },
        });
        throw err;
      }
    },
    [pollExport]
  );
};
